import React, { useMemo } from 'react'
import useApiService from 'services/api'
import LoadingScreen from 'components/Layout/LoadingScreen'
import {
  CompanyPageLayout,
  CompanyContentSections,
} from 'components/NewCompanyPage'
import { DoorsModal } from 'components/NewCompanyPage/Modals/DoorsModal/DoorsModal'
import useDocumentTitle from 'components/useDocumentTitle'
import { LodgingDoorTable } from 'components/DataExpansionComponents/GooglePlaces/LodgingDoors/LodgingDoorsTable/LodgingDoorsTable'
import {
  buildGooglePlaceSummaryContent,
  buildGooglePlaceLocationContent,
  GOOGLE_PLACE_CONFIGS,
} from './utils/googlePlacesHelpers'
import { LODGING_DOOR_TABLE_KEY } from 'constants/tableQueryKeys'

export function NewLodgingPage({ lodgingID }: { lodgingID?: number }) {
  const api = useApiService()
  const { data, isLoading } = api.useGetLodging(lodgingID)

  // Set document title
  useDocumentTitle(
    `${data?.lodging?.name || 'Not Found'} (Lodging) - First Bite`
  )

  const [showDoorsModal, setShowDoorsModal] = React.useState(false)

  const entityBreadcrumbs = useMemo(
    () =>
      data?.lodging?.name
        ? [{ title: 'Company', label: data.lodging.name }]
        : [],
    [data?.lodging?.name]
  )

  if (isLoading) {
    return <LoadingScreen />
  }

  const config = GOOGLE_PLACE_CONFIGS.lodging

  // Use helper functions to build content
  const summaryTabContent = buildGooglePlaceSummaryContent({
    placeData: data?.lodging,
    onViewDoorsClick: () => setShowDoorsModal(true),
  })

  const locationDetailsTabContent = buildGooglePlaceLocationContent(
    data?.lodging
  )

  return (
    <>
      <CompanyPageLayout
        chainProxyId={data!.id!}
        breadcrumbRoot={config.breadcrumbRoot}
        breadcrumbs={[
          {
            title: config.breadcrumbTitle,
            breadcrumb: data!.lodging!.name!,
            url: undefined,
          },
        ]}
        entityBreadcrumbs={entityBreadcrumbs}
        categoryInfo={{
          categorySlug: 'commercial',
          subCategorySlug: 'lodging-hospitality',
        }}
        sidebar={{
          header: {
            name: data!.lodging!.name!,
            domain: data!.lodging!.domain!,
            logoUrl: undefined,
          },
          tabs: [
            {
              key: 'summary',
              title: 'Summary',
              content: summaryTabContent,
            },
            {
              key: 'location-details',
              title: 'Location Details',
              content: locationDetailsTabContent,
            },
          ],
        }}
      >
        <CompanyContentSections
          chainProxyId={data!.id!}
          entityID={lodgingID}
          companyName={data!.lodging!.name!}
          websiteAddress={data!.lodging!.domain!}
          companyTypeSlug="lodging-hospitality"
          tableTypeSlug="lodging-hospitality"
        />
      </CompanyPageLayout>

      {/* Doors Modal with Tabs */}
      {data && (
        <DoorsModal
          show={showDoorsModal}
          onHide={() => setShowDoorsModal(false)}
          forChainProxyId={data.id}
          chainData={{
            id: data.id,
            name: data.lodging?.name || '',
            domain: data.lodging?.domain,
            count: data.lodging?.doors_cnt,
          }}
          chainName={data.lodging?.name}
          subCategory={{
            name: config.modalName,
            slug: config.modalSlug,
            description: config.modalName,
          }}
          matchedTableComponent={LodgingDoorTable}
          matchedTableKey={`${LODGING_DOOR_TABLE_KEY}-${data.id}`}
          matchedTableProps={{
            hideCreateCompanyButton: true,
            baseFilters: { chain_proxy_id: data.id },
          }}
          unmatchedTableKeyPrefix="unmatched-lodging-doors-table"
        />
      )}
    </>
  )
}
